import { useCallback, useMemo, useState } from 'react';

/**
 * Tracks which rows of a table have a request in flight, so each row can
 * disable its own control without greying out the rest.
 *
 * Exists because a single shared `useMutation` cannot express this:
 * `isPending`/`variables` follow only the *latest* mutate call, so gating a
 * row on them re-enables it the moment a second row starts its own edit —
 * accepting further edits computed from a still-unconfirmed optimistic status.
 *
 * `track(key, run)` marks the key in flight for the duration of `run()`'s
 * promise. Rejections are swallowed: callers report failures through their
 * mutation's own onError, and an unhandled rejection here would double-report.
 */
const useInFlightKeys = () => {
  const [keys, setKeys] = useState(() => new Set());

  const track = useCallback(async (key, run) => {
    setKeys((prev) => new Set(prev).add(key));

    try {
      await run();
    } catch {
      // Reported by the caller's mutation onError.
    } finally {
      setKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);

        return next;
      });
    }
  }, []);

  const has = useCallback((key) => keys.has(key), [keys]);

  return useMemo(() => ({ track, has }), [track, has]);
};

export default useInFlightKeys;
